// Cornell University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Cornell's Common Data Set 2025-2026 (the PDF that irp.cornell.edu posted
//    in September 2026, read alongside its Excel twin): C1 applicants/admits/
//    enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21 early decision; G1/G5 2026-2027 costs. Quirk: the PDF's
//    C21 still carries the form's old "For the Fall 2024 entering class"
//    label; the Excel file labels the same counts Fall 2025, and the 2024-2025
//    CDS gives different Fall 2024 counts (9,973 and 1,161), so they are
//    Fall 2025.
//  - Cornell IRP's University Factbook, Undergraduate admissions dashboard
//    (Tableau, filtered by college): Fall 2025 applications, acceptances and
//    entering students by college. Its July snapshot totals (72,519 and
//    6,076) differ slightly from the CDS, and it shows no Fall 2025 figure
//    for the Brooks School.
//  - admissions.cornell.edu (first-year applicants, standardized testing
//    policy, the college pages with their own requirements) and
//    finaid.cornell.edu (2026-27 cost of attendance).
//  - College Scorecard, school id 190415. The API (DEMO_KEY) was over its
//    rate limit, so the net price figures were read from the Scorecard's
//    official school page, which shows the same latest-year fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Cornell Common Data Set 2025-2026',
        url: 'https://irp.cornell.edu/wp-content/uploads/2026/09/Cornell-Common-Data-Set-2025-26.pdf',
        read: '2026-10-01',
    },
    byCollege: {
        label: 'Cornell IRP, University Factbook, Undergraduate Admissions',
        url: 'https://irp.cornell.edu/university-factbook/undergraduate-admissions',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Cornell Admissions, First-Year Applicants',
        url: 'https://admissions.cornell.edu/how-to-apply/first-year-applicants',
        read: '2026-10-01',
    },
    tests: {
        label: 'Cornell Admissions, Standardized Testing Policy',
        url: 'https://admissions.cornell.edu/policies/standardized-testing-policy',
        read: '2026-10-01',
    },
    progAAP: {
        label: 'Cornell Admissions, College of Architecture, Art, and Planning Applicants',
        url: 'https://admissions.cornell.edu/first-year-cornell-aap-applicants',
        read: '2026-10-01',
    },
    progCALS: {
        label: 'Cornell Admissions, College of Agriculture and Life Sciences Applicants',
        url: 'https://admissions.cornell.edu/first-year-cals-applicants',
        read: '2026-10-01',
    },
    progEng: {
        label: 'Cornell Admissions, Duffield College of Engineering Applicants',
        url: 'https://admissions.cornell.edu/first-year-duffield-engineering-applicants',
        read: '2026-10-01',
    },
    progHE: {
        label: 'Cornell Admissions, College of Human Ecology Applicants',
        url: 'https://admissions.cornell.edu/first-year-human-ecology-applicants',
        read: '2026-10-01',
    },
    progSCJ: {
        label: 'Cornell Admissions, SC Johnson College of Business Applicants',
        url: 'https://admissions.cornell.edu/first-year-sc-johnson-college-applicants',
        read: '2026-10-01',
    },
    costs: {
        label: 'Cornell Financial Aid, Cost to Attend',
        url: 'https://finaid.cornell.edu/cost-to-attend',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190415-Cornell-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'cornell-university',
    kbId: 'cornell_university',
    name: 'Cornell University',
    shortName: 'Cornell',
    location: 'Ithaca, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Cornell brand center); decoration only.
    branding: {
        primary: '#B31B1B',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/cornell_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 72523,
        admitted: 6077,
        enrolled: 3827,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 12978, admitted: 1720 },
            { group: 'Out-of-state', applied: 40979, admitted: 3693 },
            { group: 'International', applied: 18566, admitted: 664 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'You apply to one of Cornell\'s colleges or schools. Cornell\'s Factbook reports applications, acceptances and entering students by college for fall 2025; the rates are worked out from those counts. Its totals (72,519 applied, 6,076 accepted) come from a July snapshot and differ slightly from the Common Data Set\'s. It shows no fall 2025 figures for the Brooks School of Public Policy.',
        head: ['College', 'Applied', 'Accepted', 'Rate'],
        rows: [
            { name: 'Agriculture & Life Sciences', applied: 7974, admitted: 965, rate: 12.1 },
            { name: 'Architecture, Art & Planning', applied: 2146, admitted: 224, rate: 10.4 },
            { name: 'Arts & Sciences', applied: 29003, admitted: 2294, rate: 7.9 },
            { name: 'Dyson School', applied: 5877, admitted: 315, rate: 5.4 },
            { name: 'Engineering', applied: 20257, admitted: 1341, rate: 6.6 },
            { name: 'Human Ecology', applied: 3173, admitted: 305, rate: 9.6 },
            { name: 'Industrial & Labor Relations', applied: 1384, admitted: 217, rate: 15.7 },
            { name: 'Nolan School', applied: 1163, admitted: 259, rate: 22.3 },
        ],
        source: 'byCollege',
        where: 'Fall first-year applications, acceptances and entering students, filtered by college',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 10057, admitted: 1889 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them. They are part of the totals above. Cornell has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Cornell\'s colleges or schools, and each sets its own high school course requirements. Some ask for more:',
        rows: [
            { school: 'Architecture, Art, and Planning', requirement: 'Architecture and Fine Arts applicants submit a portfolio by the application deadline (November 1 or January 2). Architecture applicants need 4 units of math.', source: 'progAAP' },
            { school: 'Agriculture and Life Sciences', requirement: 'Landscape Architecture applicants submit a portfolio by the application deadline. Pre-calculus is required; calculus is required for ten majors and physics for four of them.', source: 'progCALS' },
            { school: 'Human Ecology', requirement: 'Design + Environmental Analysis, Fashion Design and Fashion Design Management applicants submit a design supplement through SlideRoom by the application deadline.', source: 'progHE' },
            { school: 'Duffield College of Engineering', requirement: '4 units of math including calculus, plus physics and chemistry. One of your two teacher recommendations must come from a math, science or computer science teacher.', source: 'progEng' },
            { school: 'Dyson and Nolan Schools (SC Johnson College of Business)', requirement: 'A resume is required. Dyson requires calculus if your school offers it, plus chemistry and biology. Nolan requires pre-calculus and chemistry.', source: 'progSCJ' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 2' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 2', decision: 'Late March', binding: false },
        ],
        note: 'The application, the fee or fee waiver, and any portfolio or design supplement are due on the deadline. All other required materials are due November 13 for Early Decision and January 19 for Regular Decision.',
        source: 'firstYear',
        binding: 'Early Decision is binding and meant for students who have chosen Cornell as their first choice. If you are admitted, you must withdraw all applications to other colleges and universities.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Cornell requires SAT or ACT scores from first-year applicants. You can self-report scores on the Common Application or in your applicant portal; if you enroll, you must send official scores. Cornell considers the highest section scores across test dates, and the ACT science section is not required.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by January 2.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73946 },
            { item: 'Mandatory fees', amount: 1024 },
            { item: 'Housing (on campus)', amount: 13744 },
            { item: 'Food', amount: 7604 },
            { item: 'Books and course materials', amount: 1234 },
            { item: 'Personal expenses', amount: 2182 },
        ],
        total: 99734,
        note: 'Transportation varies by region and is not in the total. New York residents in Cornell\'s state contract colleges and schools (Agriculture and Life Sciences, Dyson, Human Ecology, ILR, and the Brooks School\'s BS programs) pay $49,816 tuition, for an estimated total of $75,604. The Common Data Set gives the same costs for the endowed colleges.',
        source: 'costs',
        netPrice: {
            average: 33934,
            byIncome: [
                { income: '$0 to $30,000', amount: 6371 },
                { income: '$30,001 to $48,000', amount: 6152 },
                { income: '$48,001 to $75,000', amount: 6797 },
                { income: '$75,001 to $110,000', amount: 13422 },
                { income: 'Over $110,000', amount: 55935 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Cornell\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
